import React, { useEffect, useRef } from "react";
import classNames from "classnames";
import "./LoanContentTabBar.scss";

/**
 * [US-07] LoanContentTabBar
 * Shared loan-scoped secondary navigation for dashboard routes. Mounts in the
 * FE-02 content header slot (see GlobalNavigationShell `contentHeaderSlot`) so
 * borrowers move between Home/Tasks/Documents/Loan Details without nested
 * sidebar links. Route owner supplies `tabs` (config + optional badge count);
 * this component owns rendering, active-tab state reflection, and keyboard
 * navigation (NFR-01).
 */
export interface LoanContentTab {
  readonly id: string;
  readonly label: string;
  /** Optional per-tab badge count supplied by the route owner (eg. Dashboard pod owns Tasks count). */
  readonly badge?: number | null;
}

export interface LoanContentTabBarProps {
  readonly tabs: readonly LoanContentTab[];
  readonly activeTab: string;
  readonly onTabChange: (tabId: string) => void;
  readonly ariaLabel?: string;
}

const LoanContentTabBar: React.FC<LoanContentTabBarProps> = ({
  tabs,
  activeTab,
  onTabChange,
  ariaLabel = "Loan content navigation",
}) => {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Bug fix: on narrow mobile viewports the 4-tab row is wider than the
  // container (`.loan-content-tab-bar` is `overflow-x: auto`, see
  // ADO-541066). Without this, activating the last/widest tab (eg. "Loan
  // Details") via click or keyboard focus parks native scroll at the far
  // right edge and never recenters, leaving earlier tabs (Overview/Tasks/
  // Documents) scrolled fully out of the visible scroll viewport — they're
  // still in the DOM but untappable until the user discovers they can swipe
  // the row back, which reads as "the tab bar is stuck". Recentering the
  // active tab on every activation keeps neighboring tabs reachable.
  useEffect(() => {
    tabRefs.current[activeTab]?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [activeTab]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (tabs.length === 0) return;

    let nextIndex: number;
    switch (event.key) {
      case "ArrowRight":
        nextIndex = (index + 1) % tabs.length;
        break;
      case "ArrowLeft":
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    // tabs.length === 0 already returned above, and nextIndex is always
    // computed modulo tabs.length (or clamped to 0 / length - 1), so this is
    // always in bounds.
    const nextTab = tabs[nextIndex];

    event.preventDefault();
    onTabChange(nextTab.id);
    tabRefs.current[nextTab.id]?.focus();
  };

  return (
    <div className="loan-content-tab-bar" role="tablist" aria-label={ariaLabel} data-testid="loan-content-tab-bar">
      {tabs.map(({ id, label, badge }, index) => {
        const isActive = activeTab === id;
        return (
          <button
            key={id}
            ref={(node) => {
              tabRefs.current[id] = node;
            }}
            type="button"
            role="tab"
            id={`loan-content-tab-${id}`}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            className={classNames("loan-content-tab-bar__tab", "label-large-medium", {
              "loan-content-tab-bar__tab--active": isActive,
            })}
            onClick={() => onTabChange(id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <span className="loan-content-tab-bar__label">{label}</span>
            {typeof badge === "number" && badge > 0 && (
              <span className="loan-content-tab-bar__badge label-medium-medium">{badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default LoanContentTabBar;
